import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '@/components/seo';
import { ContactSection } from '@/components/public/ContactSection';
import { PublicPageHeading } from '@/components/public/PublicPageHeading';
import { PublicPageLayout, PublicPageSection } from '@/layouts/PublicPageLayout';
import { useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';
import { buildContactInfo } from '@/lib/constants/contactInfo';
import { AboutPortfolioHighlightsSection } from '@/routes/about/AboutPortfolioHighlightsSection';
import { AboutValuesSection } from '@/routes/about/AboutValuesSection';

const ABOUT_PARAGRAPHS = [
  'Strashin Developments Ltd. is a Toronto-based property management and redevelopment firm. We manage and redevelop properties across downtown Toronto and the Greater Toronto Area, with multi-disciplinary experience across office, multi-family residential, retail, and industrial real estate investment, management, and development.',
  'We believe the most sustainable building is the one already standing. By adaptively reusing and repurposing structures, we can often achieve greater carbon savings than new construction. Our projects incorporate high-efficiency upgrades such as reflective roofing, rooftop solar, geothermal HVAC, efficient plumbing fixtures, LED lighting and controls, and storm-water harvesting.',
  'A flagship example is 501 Alliance Avenue, a major green renovation of the former Cooper Canada sporting goods factory near Weston Road and Black Creek. Operated through 501 Alliance Investments Inc., the campus spans 315,000 sq ft and offers spaces from 2,000 to 67,000 sq ft. It is zoned Prestige Employment and accommodates industrial, office, and retail uses, with surface and underground parking.',
  "In Toronto's downtown west, we manage a portfolio of four character post-and-beam buildings totaling over 130,000 sq ft. Units range from 600 to 7,800 sq ft with surface parking, supporting startups, tech teams, creative agencies, fitness studios, legal firms, retailers, restaurants, consultants, and more.",
  'Through our parent company S Strashin and Sons Ltd., we have offered retail space since 1958. Today we operate the Bradstock and Weston properties near Weston Road and Bradstock Avenue between Sheppard Avenue and Finch Avenue, as well as the Victoria Park Plaza at Victoria Park Avenue and St Clair Avenue. Availability changes over time; contact us for current openings.',
  'Our promise is simple: deliver trouble-free, sustainable workspaces that help tenants stay productive. A hassle-free workspace is a direct contributor to business success, and we treat it that way every day.',
];

const FEATURED_PROPERTIES = [
  '460 Richmond Street West',
  '20 Maud Street',
  '20 Camden Street',
  '487 Adelaide Street West',
];

const ABOUT_SNAPSHOT = [
  { label: 'CleanTech campus', value: '315,000 sq ft' },
  { label: 'Downtown West portfolio', value: '130,000+ sq ft' },
  { label: 'Retail legacy', value: 'Since 1958' },
];

function preferText(value: string | null | undefined, fallback: string): string {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }
  return fallback;
}

function preferList<T>(value: T[] | null | undefined, fallback: T[]): T[] {
  if (Array.isArray(value) && value.length > 0) {
    return value;
  }
  return fallback;
}

export function AboutUs(): JSX.Element {
  const siteSettingsQuery = useSiteSettingsDetail();
  const siteSettings = siteSettingsQuery.data;
  const aboutContent = siteSettings?.about;
  const sharedContactLabels = siteSettings?.publicContent?.shared.contactSection;
  const contactInfo = buildContactInfo(siteSettings);
  const heroTitle = preferText(
    aboutContent?.heroTitle,
    'Toronto-based property management and redevelopment focused on adaptive reuse.',
  );
  const heroSubtitle = preferText(
    aboutContent?.heroSubtitle,
    'We bring multi-disciplinary real estate experience across office, multi-family residential, retail, and industrial properties with a commitment to sustainable, trouble-free workspaces.',
  );
  const tagline = preferText(aboutContent?.tagline, 'About Us');
  const highlightTitle = preferText(
    aboutContent?.highlightTitle,
    'Toronto Downtown West portfolio',
  );
  const highlightDescription = preferText(
    aboutContent?.highlightDescription,
    "Over 130,000 sq ft across four character post-and-beam buildings in Toronto's downtown west.",
  );
  const featuredProperties = preferList(aboutContent?.featuredProperties, FEATURED_PROPERTIES);
  const snapshotEyebrow = preferText(aboutContent?.snapshotEyebrow, 'At a glance');
  const snapshotStats = preferList(aboutContent?.snapshotStats, ABOUT_SNAPSHOT);
  const bodyHeading = preferText(aboutContent?.bodyHeading, 'Strashin Developments Ltd.');
  const bodyDescription = preferText(
    aboutContent?.bodyDescription,
    'A Toronto team managing and redeveloping properties for modern tenants across the GTA.',
  );
  const paragraphs = preferList(aboutContent?.paragraphs, ABOUT_PARAGRAPHS);
  const primaryParagraphs = paragraphs.length > 2 ? paragraphs.slice(0, 2) : paragraphs;
  const supportingParagraphs = paragraphs.length > 3 ? paragraphs.slice(2, -1) : [];
  const closingParagraph = paragraphs.length > 2 ? paragraphs[paragraphs.length - 1] : null;
  const primaryCtaLabel = preferText(aboutContent?.heroPrimaryCtaLabel, 'View availabilities');
  const primaryCtaHref = preferText(aboutContent?.heroPrimaryCtaHref, '/availabilities');
  const secondaryCtaLabel = preferText(aboutContent?.heroSecondaryCtaLabel, 'Contact us');
  const secondaryCtaHref = preferText(aboutContent?.heroSecondaryCtaHref, '/contact');
  const detailsSummaryLabel = preferText(
    aboutContent?.detailsSummaryLabel,
    'More about our portfolio and history',
  );
  const promiseLabel = preferText(aboutContent?.promiseLabel, 'Our promise');
  const contactHeading = preferText(aboutContent?.contactHeading, 'Contact Strashin Developments');
  const contactDescription = preferText(
    aboutContent?.contactDescription,
    'Connect with our team for leasing inquiries, tenant services, and redevelopment opportunities.',
  );
  const seoTitle = preferText(aboutContent?.seo?.title, 'About Us');
  const seoDescription = preferText(
    aboutContent?.seo?.description,
    'Strashin Developments Ltd. is a Toronto-based property management and redevelopment firm focused on adaptive reuse, efficient systems, and trouble-free workspaces.',
  );

  return (
    <PublicPageLayout>
      <Seo title={seoTitle} description={seoDescription} canonicalPath="/about" />

      <PublicPageSection
        tone="contrast"
        spacing="lg"
        id="about"
        aria-label="About Strashin Developments"
        containerClassName="grid gap-xl md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] md:items-center"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm font-semibold uppercase tracking-wide text-white/80">{tagline}</p>
          <h1 className="text-4xl font-semibold leading-tight text-white md:text-5xl">
            {heroTitle}
          </h1>
          <p className="max-w-3xl text-lg text-white/85">{heroSubtitle}</p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Link
              to={primaryCtaHref}
              className="inline-flex min-h-11 items-center justify-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-brand-primary transition hover:bg-white/90"
            >
              {primaryCtaLabel}
            </Link>
            <Link
              to={secondaryCtaHref}
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/40 px-5 py-2 text-sm font-semibold text-white/90 transition hover:bg-white/10"
            >
              {secondaryCtaLabel}
            </Link>
          </div>
          <div className="pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-white/70">
              {snapshotEyebrow}
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {snapshotStats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3"
                >
                  <p className="text-xs uppercase tracking-wide text-white/70">{stat.label}</p>
                  <p className="mt-2 text-base font-semibold text-white">{stat.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-3xl border border-white/15 bg-white/10 p-6 shadow-lg backdrop-blur">
          <h2 className="text-xl font-semibold text-white">{highlightTitle}</h2>
          <p className="mt-3 text-sm text-white/80">{highlightDescription}</p>
          <ul className="mt-4 grid gap-3 text-sm text-white/90 sm:grid-cols-2">
            {featuredProperties.map((property) => (
              <li
                key={property}
                className="rounded-2xl border border-white/15 bg-white/5 px-4 py-3 shadow-sm"
              >
                {property}
              </li>
            ))}
          </ul>
        </div>
      </PublicPageSection>

      <AboutValuesSection content={aboutContent?.values} />
      <AboutPortfolioHighlightsSection
        content={aboutContent?.portfolio}
        featuredProperties={featuredProperties}
      />

      <PublicPageSection tone="base" spacing="lg">
        <article className="rounded-3xl border border-surface-subtle bg-white p-6 shadow-card md:p-8">
          <PublicPageHeading
            title={bodyHeading}
            description={bodyDescription}
            layout="stacked"
            align="start"
            contentClassName="gap-3"
          />
          <div className="mt-6 grid gap-4 text-base leading-relaxed text-text-primary/80">
            {primaryParagraphs.map((paragraph) => (
              <p key={paragraph} className="text-pretty">
                {paragraph}
              </p>
            ))}
          </div>
          {supportingParagraphs.length > 0 ? (
            <details className="mt-6 rounded-2xl border border-surface-subtle bg-surface-light/80 p-5">
              <summary className="cursor-pointer text-sm font-semibold text-brand-primary">
                {detailsSummaryLabel}
              </summary>
              <div className="mt-4 grid gap-4 text-base leading-relaxed text-text-primary/80">
                {supportingParagraphs.map((paragraph) => (
                  <p key={paragraph} className="text-pretty">
                    {paragraph}
                  </p>
                ))}
              </div>
            </details>
          ) : null}
          {closingParagraph !== null ? (
            <div className="mt-6 rounded-2xl bg-surface-cream p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                {promiseLabel}
              </p>
              <p className="mt-3 text-base font-semibold text-text-primary">{closingParagraph}</p>
            </div>
          ) : null}
        </article>
      </PublicPageSection>

      <PublicPageSection tone="muted" spacing="lg" aria-label="Leadership and contact" id="contact">
        <ContactSection
          heading={contactHeading}
          description={contactDescription}
          addressLines={contactInfo.addressLines}
          phone={contactInfo.phone}
          email={contactInfo.email}
          hours={contactInfo.hours}
          contacts={contactInfo.contacts}
          mapEmbedUrl={contactInfo.mapEmbedUrl}
          mapTitle={sharedContactLabels?.mapTitle}
          labels={sharedContactLabels}
        />
      </PublicPageSection>
    </PublicPageLayout>
  );
}
